export interface StoredUser {
  id?: string;
  email?: string;
  name?: string;
  role?: string;
  isAdmin?: boolean;
  isSuperAdmin?: boolean;
}

interface ImpersonationMeta {
  domain: string;
  targetEmail: string;
  targetName?: string;
  startedAt: string;
  returnPath?: string;
  returnLabel?: string;
  actorRole?: 'admin' | 'superadmin';
}

const IMPERSONATION_TOKEN_KEY = 'trafficLiteImpersonationToken';
const IMPERSONATION_USER_KEY = 'trafficLiteImpersonationUser';
const IMPERSONATION_META_KEY = 'trafficLiteImpersonationMeta';
const ADMIN_VIEW_MODE_KEY = 'trafficLiteAdminViewMode';
const ADMIN_VIEW_DOMAIN_KEY = 'trafficLiteAdminViewDomain';

export type AdminViewMode = 'super' | 'domain';

const parseJson = <T,>(raw: string | null): T | null => {
  if (!raw) return null;
  try {
    return JSON.parse(raw) as T;
  } catch {
    return null;
  }
};

export const getEffectiveToken = (): string | null => {
  const impersonationToken = sessionStorage.getItem(IMPERSONATION_TOKEN_KEY);
  if (impersonationToken) return impersonationToken;
  return localStorage.getItem('trafficLiteToken');
};

export const getRealUser = (): StoredUser | null => {
  return parseJson<StoredUser>(localStorage.getItem('trafficLiteUser'));
};

export const isRealSuperAdmin = (): boolean => {
  const user = getRealUser();
  return Boolean(user?.isSuperAdmin) && !isImpersonating();
};

export const getAdminViewMode = (): AdminViewMode => {
  if (!isRealSuperAdmin()) return 'super';
  return sessionStorage.getItem(ADMIN_VIEW_MODE_KEY) === 'domain' ? 'domain' : 'super';
};

export const setAdminViewMode = (mode: AdminViewMode) => {
  if (mode === 'domain') {
    sessionStorage.setItem(ADMIN_VIEW_MODE_KEY, 'domain');
    return;
  }
  sessionStorage.removeItem(ADMIN_VIEW_MODE_KEY);
  sessionStorage.removeItem(ADMIN_VIEW_DOMAIN_KEY);
};

export const getAdminViewDomain = (): string => {
  return String(sessionStorage.getItem(ADMIN_VIEW_DOMAIN_KEY) || '').trim().toLowerCase();
};

export const setAdminViewDomain = (domain: string) => {
  const normalized = String(domain || '').trim().toLowerCase();
  if (!normalized || normalized === 'gmail.com') {
    sessionStorage.removeItem(ADMIN_VIEW_DOMAIN_KEY);
    return;
  }
  sessionStorage.setItem(ADMIN_VIEW_DOMAIN_KEY, normalized);
};

export const getEffectiveUser = (): StoredUser | null => {
  const impersonationUser = parseJson<StoredUser>(sessionStorage.getItem(IMPERSONATION_USER_KEY));
  if (impersonationUser) return impersonationUser;
  const user = getRealUser();
  if (user && getAdminViewMode() === 'domain') {
    return { ...user, isSuperAdmin: false, isAdmin: true };
  }
  return user;
};

export const isImpersonating = (): boolean => {
  return Boolean(
    sessionStorage.getItem(IMPERSONATION_TOKEN_KEY)
    && sessionStorage.getItem(IMPERSONATION_USER_KEY)
  );
};

export const setImpersonationSession = (payload: {
  token: string;
  user: StoredUser;
  domain: string;
  targetEmail: string;
  targetName?: string;
  returnPath?: string;
  returnLabel?: string;
  actorRole?: 'admin' | 'superadmin';
}) => {
  sessionStorage.setItem(IMPERSONATION_TOKEN_KEY, payload.token);
  sessionStorage.setItem(IMPERSONATION_USER_KEY, JSON.stringify(payload.user));
  sessionStorage.setItem(IMPERSONATION_META_KEY, JSON.stringify({
    domain: payload.domain,
    targetEmail: payload.targetEmail,
    targetName: payload.targetName,
    startedAt: new Date().toISOString(),
    returnPath: payload.returnPath,
    returnLabel: payload.returnLabel,
    actorRole: payload.actorRole
  } satisfies ImpersonationMeta));
};

export const clearImpersonationSession = () => {
  sessionStorage.removeItem(IMPERSONATION_TOKEN_KEY);
  sessionStorage.removeItem(IMPERSONATION_USER_KEY);
  sessionStorage.removeItem(IMPERSONATION_META_KEY);
};

export const clearAllAuthStorage = () => {
  clearImpersonationSession();
  sessionStorage.removeItem(ADMIN_VIEW_MODE_KEY);
  sessionStorage.removeItem(ADMIN_VIEW_DOMAIN_KEY);
  localStorage.removeItem('trafficLiteToken');
  localStorage.removeItem('trafficLiteUser');
  localStorage.removeItem('trafficLiteRole');
};

export const installAdminViewFetch = () => {
  if (typeof window === 'undefined') return;
  const flag = '__trafficLiteAdminViewFetch';
  if ((window as unknown as Record<string, boolean>)[flag]) return;
  (window as unknown as Record<string, boolean>)[flag] = true;
  const original = window.fetch.bind(window);
  window.fetch = (input: RequestInfo | URL, init?: RequestInit) => {
    if (getAdminViewMode() !== 'domain') {
      return original(input, init);
    }
    const headers = new Headers(init?.headers || (input instanceof Request ? input.headers : undefined));
    if (headers.get('X-Skip-Admin-View') === '1') {
      headers.delete('X-Skip-Admin-View');
      if (input instanceof Request) {
        return original(new Request(input, { ...init, headers }));
      }
      return original(input, { ...init, headers });
    }
    headers.set('X-Admin-View', 'domain');
    const viewDomain = getAdminViewDomain();
    if (viewDomain) headers.set('X-Admin-View-Domain', viewDomain);
    if (input instanceof Request) {
      return original(new Request(input, { ...init, headers }));
    }
    return original(input, { ...init, headers });
  };
};

export const getImpersonationMeta = (): ImpersonationMeta | null => {
  return parseJson<ImpersonationMeta>(sessionStorage.getItem(IMPERSONATION_META_KEY));
};
